import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { noop } from '@amazon/vinyl-util'
import { PENDING_INSTRUMENTED_GLOBAL } from '@amzn/prerender-contract'
import { APP_TIMINGS_GROUP, getPerformance } from '@/perf/performanceApi'

const pendingInstrumented = data(0)

/**
 * Number of `instrument`-wrapped async calls currently in flight (incremented
 * when the inner promise starts, decremented when it settles). The static
 * prerenderer watches this to detect quiescence — all in-flight instrumented
 * async (gql etc.) has settled — so it can decide the page is done rendering
 * without a fixed wait. Live app behavior is unaffected: this is a cheap
 * counter side-effect only.
 */
export const pendingInstrumentedCount$: ObservableValue<number> = pendingInstrumented

// Publish the counter for the static prerenderer. The render imports the
// bundled app, so it can't reach this module's export directly — expose the
// observable on the boot global the worker watches (see prerender-contract).
;(globalThis as unknown as Record<string, unknown>)[PENDING_INSTRUMENTED_GLOBAL] =
    pendingInstrumentedCount$

/**
 * Wraps an async fn with instrumentation: draws a DevTools timeStamp bar for its
 * span (under {@link APP_TIMINGS_GROUP}, in `trackName`'s lane when given) and
 * tracks the in-flight count on {@link pendingInstrumentedCount$}.
 *
 * @param name label for the timeStamp bar.
 * @param inner the async fn to instrument.
 * @param trackName DevTools lane to attribute the bar to (e.g. a track's load
 *   journey), or omitted for the shared group's default lane. A function is
 *   evaluated at draw time with the resolved value, so a caller can name the
 *   lane after the response (e.g. the createQueue span after the resolved track).
 */
export function instrument<F extends (...args: any[]) => Promise<unknown>>(
    name: string,
    inner: F,
    trackName?: string | ((value: unknown) => string | undefined)
): F {
    return ((...args) => {
        const perf = getPerformance()
        const start = perf.now()
        pendingInstrumented.value = pendingInstrumented.value + 1
        const settle = (): void => {
            pendingInstrumented.value = pendingInstrumented.value - 1
        }
        const result = inner(...args)
        // Decrement on BOTH resolve and reject; keep the returned promise the
        // original (callers get the unchanged value/rejection). `void` marks
        // the tracking chain intentionally unawaited for no-floating-promises.
        result
            .then(
                (value) => {
                    const lane = typeof trackName === 'function' ? trackName(value) : trackName
                    perf.timeStamp({
                        label: name,
                        start,
                        end: perf.now(),
                        trackGroup: APP_TIMINGS_GROUP,
                        ...(lane ? { trackName: lane } : null),
                    })
                    settle()
                },
                () => {
                    settle()
                }
            )
            .catch(noop)
        return result
    }) as F
}
